import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import {
  projectTypeMapping,
  ProjectTypeOption,
} from "../../utils/projectTypeMapping";
import DeletableChip from "../Layout/filters/DeletableChip";

interface VirtualizedProjectTypeDropdownProps {
  selectedProjectTypes: string[];
  onProjectTypesChange: (projectTypes: string[]) => void;
  label?: string;
}

// Custom option component for better performance
const ProjectTypeOptionComponent = React.memo(
  ({ option, props }: { option: ProjectTypeOption; props: any }) => (
    <Box
      component="li"
      {...props}
      sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
    >
      <Box>
        <Typography variant="body2" component="span" sx={{ color: "#ffffff" }}>
          {option.displayName}
        </Typography>
      </Box>
    </Box>
  )
);

const VirtualizedProjectTypeDropdown: React.FC<
  VirtualizedProjectTypeDropdownProps
> = ({
  selectedProjectTypes,
  onProjectTypesChange,
  label = "Project Types",
}) => {
  const [inputValue, setInputValue] = useState("");

  // Optimized filtering with smart ranking for better UX
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) {
      return projectTypeMapping.getAllOptions().slice(0, 100); // Show first 100 by default
    }

    const searchTerm = inputValue.toLowerCase();

    // Filter matching options
    const matches = projectTypeMapping.searchByName(searchTerm);

    // Smart ranking: prioritize exact starts, then contains
    const ranked = matches.sort((a, b) => {
      const aDisplayStartsWith = a.displayName
        .toLowerCase()
        .startsWith(searchTerm);
      const bDisplayStartsWith = b.displayName
        .toLowerCase()
        .startsWith(searchTerm);

      // Priority 1: Display name starts with query
      if (aDisplayStartsWith && !bDisplayStartsWith) return -1;
      if (!aDisplayStartsWith && bDisplayStartsWith) return 1;

      // Priority 2: Alphabetical by display name
      return a.displayName.localeCompare(b.displayName);
    });

    return ranked.slice(0, 200); // Limit to 200 results for performance
  }, [inputValue]);

  const selectedOptions = useMemo(() => {
    return projectTypeMapping
      .getAllOptions()
      .filter((option) => selectedProjectTypes.includes(option.id));
  }, [selectedProjectTypes]);

  const handleChange = useCallback(
    (event: any, newValue: ProjectTypeOption[]) => {
      const newIds = newValue.map((option) => option.id);
      onProjectTypesChange(newIds);
    },
    [onProjectTypesChange]
  );

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Autocomplete
      multiple
      id="project-types-autocomplete"
      options={filteredOptions}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option: ProjectTypeOption) => option.displayName}
      isOptionEqualToValue={(
        option: ProjectTypeOption,
        value: ProjectTypeOption
      ) => option.id === value.id}
      filterOptions={(options) => options} // We handle filtering manually for performance
      limitTags={3}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={
            selectedOptions.length === 0 ? "Search project types..." : ""
          }
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              color: "#e0e0e0",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            },
            shrink: selectedOptions.length > 0 || inputValue.length > 0,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1e1e1e",
              "& fieldset": {
                borderColor: "#555",
              },
              "&:hover fieldset": {
                borderColor: "#777",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
            "& .MuiInputBase-input": {
              color: "#e0e0e0",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#888888",
              opacity: 1,
            },
            "& .MuiFormHelperText-root": {
              color: "#888888",
              fontSize: "0.75rem",
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            paddingTop: "2px",
          }}
        >
          {tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <DeletableChip
                key={option.id}
                label={option.displayName}
                onDelete={() => {
                  if (tagProps.onDelete) {
                    tagProps.onDelete({} as any);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      renderOption={(props, option: ProjectTypeOption) => (
        <ProjectTypeOptionComponent
          key={option.id}
          option={option}
          props={props}
        />
      )}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxHeight: "300px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            '&[aria-selected="true"]': {
              backgroundColor: "#1976d2 !important",
            },
            "&.Mui-focused": {
              backgroundColor: "#333333 !important",
            },
          },
        },
        "& .MuiAutocomplete-popper": {
          "& .MuiPaper-root": {
            backgroundColor: "background.raised",
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
    />
  );
};

export default VirtualizedProjectTypeDropdown;
